import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ViewMode } from '../App';

interface SEOManagerProps {
  viewMode: ViewMode;
}

interface SEOMetadata {
  title: string;
  description: string;
  keywords: string;
}

const SEO_DATA: Record<string, Record<ViewMode, SEOMetadata>> = {
  pt: {
    empresa: {
      title: 'MotoRush B2B | Logística Corporativa e Estafetas com SLA em Luanda',
      description: 'Solução líder de logística B2B em Luanda: estafetas dedicados, tabela com preço fixo sem tarifa dinâmica, SLA garantido e faturação mensal pós-paga a 30 dias.',
      keywords: 'logística B2B Luanda, estafetas corporativos, SLA corporativo Luanda, estafeta dedicado Angola, faturação mensal pós-paga, frota empresarial Luanda, entregas farmácias Talatona'
    },
    pessoal: {
      title: 'MotoRush Angola | Entregas Rápidas, Estafetas e Moto Táxi em Luanda',
      description: 'Entregas expressas de encomendas e documentos em Luanda e moto táxi ágil para driblar o trânsito. Rápido, seguro e com preço justo sem surpresas.',
      keywords: 'entregas rápidas Luanda, estafeta Luanda, motoboy Angola, moto táxi Luanda, enviar encomenda Luanda, entregas Talatona, entregas Maianga, entregas Kilamba, estafeta express'
    },
    about: {
      title: 'Sobre a MotoRush | A Revolução da Mobilidade e Logística em Luanda',
      description: 'Conheça a história, missão e frota certificada da MotoRush Angola: transformando a mobilidade urbana e as entregas expressas em Luanda.',
      keywords: 'sobre a MotoRush, empresa de estafetas Luanda, frota certificada Angola, história MotoRush, fundadores MotoRush, logística Luanda'
    },
    help: {
      title: 'Central de Ajuda e FAQ | Perguntas Frequentes MotoRush Angola',
      description: 'Tire as suas dúvidas sobre entregas, pedidos de estafetas, formas de pagamento, contas B2B para empresas e suporte oficial MotoRush.',
      keywords: 'ajuda MotoRush, suporte MotoRush, FAQ entregas Luanda, dúvidas estafetas Angola, contacto MotoRush WhatsApp'
    },
    privacy: {
      title: 'Política de Privacidade | MotoRush Angola',
      description: 'Conheça a nossa política de privacidade e proteção de dados pessoais na utilização dos serviços da MotoRush em Angola.',
      keywords: 'política de privacidade MotoRush, dados pessoais, termos MotoRush Angola'
    },
    terms: {
      title: 'Termos de Uso e Condições Gerais | MotoRush Angola',
      description: 'Consulte os termos e condições de utilização da plataforma de entregas e mobilidade MotoRush em Luanda, Angola.',
      keywords: 'termos de uso MotoRush, condições de serviço, regulamento entregas Angola'
    },
    cookies: {
      title: 'Política de Cookies | MotoRush Angola',
      description: 'Informações sobre a política de cookies e preferências de navegação na plataforma online MotoRush.',
      keywords: 'política de cookies MotoRush, cookies de navegação'
    }
  },
  en: {
    empresa: {
      title: 'MotoRush B2B | Corporate Logistics & Dedicated Couriers in Luanda',
      description: 'Premier B2B logistics in Luanda, Angola: dedicated courier fleets, flat pricing with zero surge pricing, corporate SLA and 30-day post-paid billing.',
      keywords: 'B2B logistics Luanda, corporate courier Angola, dedicated motorcycle fleet Luanda, Talatona business delivery'
    },
    pessoal: {
      title: 'MotoRush Angola | Fast Delivery & Motorcycle Taxi in Luanda',
      description: 'Express parcel and document deliveries in Luanda and fast motorbike rides to beat urban traffic. Fast, safe, and transparently priced.',
      keywords: 'fast delivery Luanda, motorcycle courier Angola, express delivery Talatona, moto taxi Luanda'
    },
    about: {
      title: 'About MotoRush | Redefining Urban Mobility & Logistics in Luanda',
      description: 'Learn about MotoRush Angola, our mission, values, and vision to bring reliable on-demand courier and mobility services to Luanda.',
      keywords: 'about MotoRush, logistics company Luanda, Angola mobility startup'
    },
    help: {
      title: 'Help Center & FAQ | MotoRush Angola Support',
      description: 'Answers to frequently asked questions about rides, courier deliveries, corporate accounts, and official customer support.',
      keywords: 'MotoRush support, help center Luanda, delivery questions Angola'
    },
    privacy: {
      title: 'Privacy Policy | MotoRush Angola',
      description: 'Learn how MotoRush collects, uses, and protects your personal information in Angola.',
      keywords: 'privacy policy MotoRush Angola'
    },
    terms: {
      title: 'Terms of Service | MotoRush Angola',
      description: 'Terms and conditions governing the use of the MotoRush delivery and mobility platform.',
      keywords: 'terms of service MotoRush Angola'
    },
    cookies: {
      title: 'Cookie Policy | MotoRush Angola',
      description: 'Learn how MotoRush uses cookies to enhance your browsing experience.',
      keywords: 'cookie policy MotoRush'
    }
  }
};

const SEOManager: React.FC<SEOManagerProps> = ({ viewMode }) => {
  const { i18n } = useTranslation();
  const lang = (i18n.language || 'pt').substring(0, 2) === 'en' ? 'en' : 'pt';

  useEffect(() => {
    const meta = SEO_DATA[lang]?.[viewMode] || SEO_DATA.pt[viewMode] || SEO_DATA.pt.pessoal;

    // 1. Update Title
    document.title = meta.title;

    // 2. Update Meta Description
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', meta.description);

    // 3. Update Meta Keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', meta.keywords);

    // 4. Update OpenGraph Tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', meta.title);

    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', meta.description);

    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) twitterTitle.setAttribute('content', meta.title);

    const twitterDesc = document.querySelector('meta[name="twitter:description"]');
    if (twitterDesc) twitterDesc.setAttribute('content', meta.description);

    // 5. Update Canonical Tag
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      const baseUrl = 'https://motorush-landing-page.vercel.app/';
      canonicalLink.setAttribute('href', baseUrl);
    }
  }, [viewMode, lang]);

  return null;
};

export default SEOManager;
